/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * RankBadge — "3rd of 16" style pill showing where an entity stands
 * in a peer group. The color intensity follows the percentile so
 * rank-1 always reads hot and last-place always reads cold.
 *
 *   <RankBadge rank={3} total={16} />
 *   <RankBadge rank={12} total={16} label="LD rank" />
 *
 * Use alongside DeltaChip: Delta answers "how much better/worse?",
 * Rank answers "how many peers are better than me?".
 */
import React from 'react';

type Props = {
  /** 1-indexed rank. 1 = best. */
  rank: number | null | undefined;
  /** Total entities in the peer group. */
  total: number;
  /** Optional label prefix (e.g. 'LD rank'). */
  label?: string;
  /** Invert coloring when lower rank numbers are actually bad (unusual). */
  direction?: 'higher-better' | 'lower-better';
  className?: string;
};

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export const RankBadge: React.FC<Props> = ({
  rank,
  total,
  label,
  direction = 'higher-better',
  className = '',
}) => {
  if (rank == null || Number.isNaN(rank) || total <= 0) return null;

  // Percentile: 1.0 = best, 0.0 = worst. direction flip reverses.
  const raw = (total - rank + 1) / total;
  const score = direction === 'higher-better' ? raw : 1 - raw;

  let cls: string;
  if (score >= 0.8) cls = 'bg-tier-anchor/20 text-tier-anchor border-tier-anchor/40';
  else if (score >= 0.55) cls = 'bg-tier-hot/20 text-tier-hot border-tier-hot/40';
  else if (score >= 0.3) cls = 'bg-tier-warm/15 text-tier-warm border-tier-warm/40';
  else cls = 'bg-tier-cool/20 text-tier-cool border-tier-cool/40';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${cls} ${className}`}
      title={`${label ? label + ': ' : ''}${ordinal(rank)} of ${total}`}
    >
      <span>#{rank}</span>
      <span className="text-fg-secondary font-normal">of {total}</span>
      {label && <span className="text-fg-secondary font-normal">· {label}</span>}
    </span>
  );
};

export default RankBadge;
